import api from '@/utils/api';
import {TimesheetEditQueue, equal} from '@/utils/timesheetEditQueue';

const copy = value => JSON.parse(JSON.stringify(value));

export default {
    data: () => ({editStates: {}, rowDraftGenerations: {}, editDirtyCount: 0, editPendingCount: 0, editErrorCount: 0, editUnsavedCount: 0,
        arrivalCancelledMessage: '', batchEditing: false, editSyncPending: false, editResolutionPending: false}),
    created() {
        this._ownTimesheetMutations = new Set();
        this._editSheetId = null;
        this._confirmedSheetVersion = 0;
        this._lineIssueCache = new Map();
        this._gridLineCache = new Map();
        this._assignmentLineCache = new Map();
        this._assessmentLineCache = new Map();
        this._deferredDraftLines = new Set();
    },
    mounted() { window.addEventListener('beforeunload', this.warnUnsavedTimesheet); },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.warnUnsavedTimesheet);
        this.resetEditQueue();
    },
    computed: {
        editCancelledKeys() {
            return Object.keys(this.editStates).filter(key => this.editStates[key].error && this.editStates[key].error.cancelled);
        },
        editProblems() {
            if (!this.editErrorCount) return [];
            return Object.keys(this.editStates).filter(key => this.editStates[key].error && !this.editStates[key].error.cancelled).map(key => {
                const record = this._editQueue && this._editQueue.records.get(key);
                const line = record && this.sheet && this.sheet.lines.find(item => Number(item.id) === record.meta.lineId);
                return {key, label: `${line && line.employee_name_snapshot || record && record.meta.name || 'Сотрудник'} · ${record && record.meta.date || 'КТУ / характеристика'}`,
                    value: record && (record.meta.date ? (record.proposal === undefined ? record.value : record.proposal) : this.assessmentDraftLabel(record.value)), message: this.editStates[key].error.message};
            });
        }
    },
    methods: {
        assessmentDraftLabel(value) {
            const values = value.values || value.characteristic_values || {};
            const result = (this.characteristicFields || []).filter(field => values[field.key] != null && values[field.key] !== '')
                .map(field => `${field.label}: ${values[field.key] === true ? 'Да' : values[field.key] === false ? 'Нет' : values[field.key]}`);
            if (value.ktu !== undefined && value.ktu !== '') result.unshift(`КТУ: ${value.ktu == null ? 'не указан' : value.ktu}`);
            if (value.ktu_comment) result.push(`Пояснение КТУ: ${value.ktu_comment}`);
            if (value.ktu_evidence && value.ktu_evidence.length) result.push(`Основания КТУ: ${value.ktu_evidence.map(item => item.label || item.key || item).join('; ')}`);
            return result.join('\n') || 'Поля очищены';
        },
        warnUnsavedTimesheet(event) {
            if (!this.batchEditing && !this.editDirtyCount && !this.editPendingCount && !this.editErrorCount) return;
            event.preventDefault();
            event.returnValue = '';
        },
        resetEditQueue() {
            if (this._editRefreshTimer) clearTimeout(this._editRefreshTimer);
            this._editRefreshTimer = null;
            if (this._editQueue) this._editQueue.dispose();
            this._editQueue = null;
            this._editSheetId = null;
            this._confirmedSheetVersion = 0;
            this._lineIssueCache = new Map();
            this._gridLineCache = new Map();
            this._assignmentLineCache = new Map();
            this._assessmentLineCache = new Map();
            this._deferredDraftLines = new Set();
            this.editStates = {};
            this.rowDraftGenerations = {};
            this.editDirtyCount = this.editPendingCount = this.editErrorCount = 0;
            this.editUnsavedCount = 0;
            this.arrivalCancelledMessage = '';
            this.periodSaveCount = 0;
            this.periodSaveFailed = false;
            this.editSyncPending = false;
            if (this._ownTimesheetMutations) this._ownTimesheetMutations.clear();
        },
        ensureEditQueue() {
            const sheetId = Number(this.sheet && this.sheet.timesheet.id);
            if (this._editQueue && this._editSheetId === sheetId) return this._editQueue;
            this.resetEditQueue();
            this._editSheetId = sheetId;
            this._confirmedSheetVersion = Number(this.sheet && this.sheet.timesheet.version || 0);
            this.initializeEditStates();
            this._editQueue = new TimesheetEditQueue({
                send: (record, value, version) => this.sendTimesheetEdit(sheetId, record, value, version),
                sendBatch: edits => this.sendTimesheetBatch(sheetId, edits),
                describeError: error => ({
                    cancelled: error && error.code === 'TIMESHEET_ARRIVAL_CONFIRMATION_CANCELLED',
                    message: error && error.code === 'TIMESHEET_ARRIVAL_CONFIRMATION_CANCELLED' ? error.message
                        : this.responseMessage(error, 'Не удалось сохранить. Введённое значение оставлено в ячейке.'),
                    conflict: Number(error && error.response && error.response.status) === 409
                        && (error.response.data || {}).code !== 'TIMESHEET_ARRIVAL_CONFIRMATION_REQUIRED'
                }),
                onState: (record, state) => {
                    const old = this.editStates[record.key] || {};
                    this.editDirtyCount += Number(state.dirty) - Number(Boolean(old.dirty));
                    this.editPendingCount += Number(state.pending) - Number(Boolean(old.pending));
                    this.editErrorCount += Number(Boolean(state.error && !state.error.cancelled)) - Number(Boolean(old.error && !old.error.cancelled));
                    this.editUnsavedCount += Number(!state.pending && Boolean(state.dirty || state.error))
                        - Number(!old.pending && Boolean(old.dirty || old.error));
                    if (!equal(old, state)) this.$set(this.editStates, record.key, state);
                    this.periodSaveCount = this.editPendingCount;
                    this.periodSaveFailed = this.editErrorCount > 0;
                },
                onCancelled: (record, unchanged) => {
                    this.arrivalCancelledMessage = 'Часы не внесены: прибытие не подтверждено';
                    if (unchanged && record.meta.date) this.$set(this.monthCellDrafts, record.key, record.value);
                },
                onAccepted: (record, result, unchanged) => this.acceptTimesheetEdit(record, result, unchanged),
                onIdle: () => this.scheduleEditRefresh()
            });
            return this._editQueue;
        },
        initializeEditStates() {
            // Existing reactive properties let one editor change without invalidating every editor.
            if (!this.sheet) return;
            this.sheet.lines.forEach(line => {
                if (this.rowDraftGenerations[line.id] === undefined) this.$set(this.rowDraftGenerations, line.id, 0);
                [...this.monthDays.map(day => this.cellKey(line.id, day.date)), `detail:${line.id}`].forEach(key => {
                    if (!this.editStates[key]) this.$set(this.editStates, key, {dirty: false, pending: false, error: null});
                });
            });
        },
        editStateClass(key) {
            const state = this.editStates[key] || {};
            return {'is-save-pending': state.pending, 'is-save-error': Boolean(state.error && !state.error.cancelled)};
        },
        editStateTitle(key) {
            const state = this.editStates[key] || {};
            return state.error ? `Не сохранено: ${state.error.message}` : state.pending ? 'Сохраняется…' : state.dirty ? 'Не сохранено — завершите ввод' : '';
        },
        detailEditValue(line) {
            if (line.shared_characteristic) return {ktu: line.ktu == null ? '' : String(Number(line.ktu)),
                ktu_evidence: (line.ktu_evidence || []).map(item => String(item.key || item)),
                ktu_comment: line.ktu_comment || ''};
            return {ktu: line.ktu === null || line.ktu === undefined ? '' : String(Number(line.ktu)),
                ktu_evidence: (line.ktu_evidence || []).map(item => String(item && item.key || '')).filter(Boolean),
                characteristic_values: this.characteristicFields.reduce((values, field) => {
                    if (values[field.key] === undefined) values[field.key] = '';
                    return values;
                }, Object.assign({}, line.characteristic_values || {}))};
        },
        ensureCellEdit(line, date) {
            const queue = this.ensureEditQueue();
            const key = this.cellKey(line.id, date);
            const entry = this.entryFor(line, date);
            queue.ensure(key, this.entryDisplay(entry), {version: Number(entry && entry.version || 0), id: Number(entry && entry.id || 0)},
                {lineId: Number(line.id), date, name: line.employee_name_snapshot});
            return key;
        },
        trackCellInput(line, date) {
            const key = this.ensureCellEdit(line, date);
            if (this._editQueue.records.get(key).value !== (this.monthCellDrafts[key] || '')) this.rowDraftGenerations[line.id] += 1;
            this._editQueue.update(key, this.monthCellDrafts[key] || '');
        },
        trackDetailInput(line) {
            const queue = this.ensureEditQueue();
            const key = `detail:${line.id}`;
            queue.ensure(key, this.detailEditValue(line), Number(line.details_version || 1), {lineId: Number(line.id), name: line.employee_name_snapshot});
            const draft = this.lineDetailDrafts[line.id];
            if (draft) {
                const value = line.shared_characteristic
                    ? {ktu: draft.ktu, ktu_evidence: draft.ktu_evidence, ktu_comment: draft.ktu_comment || ''}
                    : {ktu: draft.ktu, ktu_evidence: draft.ktu_evidence, characteristic_values: draft.characteristic_values};
                if (!equal(queue.records.get(key).value, value)) this.rowDraftGenerations[line.id] += 1;
                queue.update(key, value);
            }
            return key;
        },
        trackEditPromise(promise) {
            if (this._periodSaves) {
                this._periodSaves.add(promise);
                promise.then(() => this._periodSaves.delete(promise), () => this._periodSaves.delete(promise));
            }
            return promise;
        },
        queueCellSave(line, date) {
            if (!this.canEdit || this.batchEditing || this.hoursArrivalRequirement && this.plannedArrivalModalVisible) return Promise.resolve(false);
            this.arrivalCancelledMessage = '';
            this.trackCellInput(line, date);
            return this.trackEditPromise(this._editQueue.save(this.cellKey(line.id, date)));
        },
        queueDetailsSave(line) {
            if (!this.canEdit) return Promise.resolve(false);
            const key = this.trackDetailInput(line);
            return this.trackEditPromise(this._editQueue.save(key));
        },
        async saveCellsBatch(cells, text) {
            if (!this.canEdit || this.batchEditing || this.editErrorCount || this.hoursArrivalRequirement && this.plannedArrivalModalVisible) return false;
            this.arrivalCancelledMessage = '';
            const keys = cells.map(({line, date}) => this.ensureCellEdit(line, date));
            this.batchEditing = true;
            try { return await this.trackEditPromise(this._editQueue.saveBatch(keys, text)); }
            finally { this.batchEditing = false; }
        },
        resumeCancelledArrivals() {
            if (!this.canEdit || this.editPendingCount || !this.editCancelledKeys.length) return;
            this.bulkError = '';
            return this.trackEditPromise(this._editQueue.saveBatch(this.editCancelledKeys));
        },
        async sendTimesheetBatch(sheetId, edits) {
            const entries = edits.map(({record, value, version}) => Object.assign({line_id: record.meta.lineId,
                work_date: record.meta.date, version: version.version, expected_entry_id: version.id}, this.parseMonthCell(value)));
            const response = await this.putDayEntriesWithArrivals(sheetId, entries, {headers: this.editSessionHeaders(true, 'batch')});
            return edits.map(({record}) => this.dayEntrySaveResult(response, record));
        },
        dayEntrySaveResult(response, record) {
            const result = (response.data.entries || []).find(item => Number(item.line_id) === record.meta.lineId && item.work_date === record.meta.date);
            if (!result) throw new Error('Сервер не подтвердил значение ячейки. Обновите приложение.');
            return {value: this.entryDisplay(result.entry), version: {version: Number(result.entry && result.entry.version || 0),
                id: Number(result.entry && result.entry.id || 0)}, entry: result.entry, sheetVersion: response.data.timesheet_version};
        },
        async sendTimesheetEdit(sheetId, record, value, version) {
            const mutationId = `cell:${sheetId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
            this._ownTimesheetMutations.add(mutationId);
            if (this._ownTimesheetMutations.size > 300) this._ownTimesheetMutations.delete(this._ownTimesheetMutations.values().next().value);
            const config = {headers: Object.assign({}, this.editSessionHeaders(), {'x-timesheet-mutation-id': mutationId}), ignoreModal: true};
            if (record.meta.date) {
                const parsed = this.parseMonthCell(value);
                const response = await this.putDayEntriesWithArrivals(sheetId, [Object.assign({
                    line_id: record.meta.lineId, work_date: record.meta.date, version: version.version,
                    expected_entry_id: version.id
                }, parsed)], config);
                return this.dayEntrySaveResult(response, record);
            }
            const response = await api.patch(`/api/timesheets/${sheetId}/lines/${record.meta.lineId}`, {
                ktu: this.ktuEnabled && value.ktu !== '' ? value.ktu : null,
                ktu_evidence: value.ktu_evidence, ktu_comment: value.ktu_comment, characteristic_values: value.characteristic_values, version
            }, config);
            const currentLine = this.sheet.lines.find(line => Number(line.id) === record.meta.lineId);
            if (currentLine && currentLine.shared_characteristic) response.data.shared_characteristic = currentLine.shared_characteristic;
            return {value: this.detailEditValue(response.data), version: Number(response.data.details_version),
                details: response.data, sheetVersion: response.data.timesheet_version};
        },
        acceptTimesheetEdit(record, result, unchanged) {
            const line = this.sheet && this.sheet.lines.find(item => Number(item.id) === record.meta.lineId);
            if (!line) return;
            this._confirmedSheetVersion = Math.max(this._confirmedSheetVersion, Number(result.sheetVersion || 0));
            this.sheet.timesheet.version = this._confirmedSheetVersion;
            if (record.meta.date) {
                const entries = (line.entries || []).filter(entry => entry.work_date !== record.meta.date);
                if (result.entry) entries.push(result.entry);
                this.$set(line, 'entries', entries);
                // The authoritative aggregate arrives in the coalesced refresh.
                this.$set(line, 'totals', {});
                if (unchanged) this.$set(this.monthCellDrafts, record.key, result.value);
            } else {
                ['ktu', 'ktu_evidence', 'ktu_comment', 'details_version'].concat(line.shared_characteristic ? [] : ['characteristic_values']).forEach(key => this.$set(line, key, result.details[key]));
                const draft = this.lineDetailDrafts[line.id];
                if (unchanged) this.$set(this.lineDetailDrafts, line.id, Object.assign({}, draft, copy(result.value), {version: result.version}));
                else if (draft) draft.version = result.version;
            }
            this.editSyncPending = true;
        },
        scheduleEditRefresh() {
            if (!this.editSyncPending || this._isDestroyed) return;
            if (this._editRefreshTimer) clearTimeout(this._editRefreshTimer);
            this._editRefreshTimer = setTimeout(async () => {
                this._editRefreshTimer = null;
                if (!this.sheet || this.editPendingCount) return;
                const queue = this._editQueue;
                const epoch = queue && queue.epoch;
                try {
                    const loaded = await this.loadTimesheet(this.sheet.timesheet.id, {fromRealtime: true});
                    if (loaded && queue === this._editQueue && (!queue || epoch === queue.epoch)) this.editSyncPending = false;
                } catch (error) {
                    this.errorMessage = 'Отметки сохранены. Не удалось обновить итоги; повторите обновление табеля.';
                }
            }, 400);
        },
        mergeTimesheetDrafts(previous, next) {
            if (!previous || Number(previous.timesheet.id) !== Number(next.timesheet.id)) {
                this.resetEditQueue();
                this.sheet = next;
                this.rebuildDrafts(); this.rebuildMonthDrafts(); this.rebuildLineDetailDrafts();
                this.ensureEditQueue();
                return;
            }
            const oldLines = new Map(previous.lines.map(line => [Number(line.id), line]));
            const months = this.monthCellDrafts;
            const details = this.lineDetailDrafts;
            const queue = this._editQueue;
            const focused = this.editingPresenceKey;
            Object.keys(next).forEach(key => {
                if (key !== 'lines' && equal(previous[key], next[key])) next[key] = previous[key];
            });
            next.lines = next.lines.map(line => {
                const old = oldLines.get(Number(line.id));
                return old && equal(old, line) ? old : line;
            });
            if (next.lines.length === previous.lines.length && next.lines.every((line, index) => line === previous.lines[index])) {
                next.lines = previous.lines;
            }
            // Polling an unchanged sheet must not invalidate every Vue computed
            // through a new root object or initialize a thousand drafts again.
            const unchanged = Object.keys(previous).length === Object.keys(next).length
                && Object.keys(next).every(key => next[key] === previous[key]);
            if (unchanged && !this._deferredDraftLines.size) return;
            if (!unchanged) this.sheet = next;
            this._confirmedSheetVersion = Math.max(this._confirmedSheetVersion, Number(next.timesheet.version || 0));
            if (!unchanged) { this.initializeEditStates(); this.rebuildDrafts(); }
            next.lines.forEach(line => {
                if (line === oldLines.get(Number(line.id)) && !this._deferredDraftLines.has(line.id)) return;
                this._deferredDraftLines.delete(line.id);
                this.monthDays.forEach(day => {
                    const key = this.cellKey(line.id, day.date);
                    const entry = this.entryFor(line, day.date);
                    const value = this.entryDisplay(entry);
                    const record = queue && queue.records.get(key);
                    if (record && focused === this.entryPresenceKey(line.id, day.date)) {
                        this._deferredDraftLines.add(line.id);
                        return;
                    }
                    if (record && !queue.merge(key, value, {version: Number(entry && entry.version || 0), id: Number(entry && entry.id || 0)})) return;
                    if (months[key] !== value) this.$set(months, key, value);
                });
                const key = `detail:${line.id}`;
                if (line.shared_characteristic) this.mergeSharedCharacteristic(line);
                const value = this.detailEditValue(line);
                const record = queue && queue.records.get(key);
                if (record && String(focused || '').startsWith(`detail:${line.id}:`)) {
                    this._deferredDraftLines.add(line.id);
                    return;
                }
                if (record && !queue.merge(key, value, Number(line.details_version || 1))) return;
                const draft = Object.assign({}, details[line.id], value, {version: Number(line.details_version || 1)});
                if (!equal(details[line.id], draft)) this.$set(details, line.id, draft);
            });
            // A removed line must not silently discard a pending local draft.
            const lineIds = new Set(next.lines.map(line => Number(line.id)));
            this._deferredDraftLines.forEach(id => { if (!lineIds.has(Number(id))) this._deferredDraftLines.delete(id); });
            if (queue) queue.records.forEach(record => {
                if (lineIds.has(record.meta.lineId)) return;
                if (queue.dirty(record) || record.error) {
                    record.error = {message: 'Строка удалена другим пользователем. Введённое значение сохранено в черновике.', conflict: true};
                    queue.notify(record);
                }
            });
        },
        discardRemovedLineDrafts(lineId) {
            if (this._editQueue) this._editQueue.discardLine(lineId);
            const remove = (map, key) => { if (Object.prototype.hasOwnProperty.call(map, key)) this.$delete(map, key); };
            Object.keys(this.monthCellDrafts).filter(key => key.startsWith(`${lineId}:`))
                .forEach(key => remove(this.monthCellDrafts, key));
            Object.keys(this.editStates).filter(key => key.startsWith(`${lineId}:`) || key === `detail:${lineId}` || key === `characteristic:${lineId}`)
                .forEach(key => remove(this.editStates, key));
            remove(this.lineDetailDrafts, lineId);
            remove(this.entryDrafts, lineId);
            remove(this.rowDraftGenerations, lineId);
            if (this._deferredDraftLines) this._deferredDraftLines.delete(Number(lineId));
            // Remove just this row's persisted assessment; other employees' drafts stay intact.
            const key = this.assessmentDraftKey({id: lineId});
            if (key && typeof localStorage !== 'undefined') {
                try {
                    localStorage.removeItem(key);
                    const backups = [];
                    for (let i = 0; i < localStorage.length; i++) {
                        const candidate = localStorage.key(i);
                        if (candidate && candidate.startsWith(`${key}:pending:`)) backups.push(candidate);
                    }
                    backups.forEach(candidate => localStorage.removeItem(candidate));
                } catch (_) { /* The removed row is not restored from storage. */ }
            }
            this.sheet.lines = this.sheet.lines.filter(line => Number(line.id) !== Number(lineId));
            this.sheetRequestId += 1;
        },
        async flushTimesheetEdits() {
            if (typeof document !== 'undefined' && document.activeElement && this.$el.contains(document.activeElement)) document.activeElement.blur();
            await this.$nextTick();
            if (this._editQueue) {
                const saves = [];
                this._editQueue.records.forEach(record => {
                    if (this._editQueue.dirty(record) && !record.error) saves.push(this._editQueue.save(record.key));
                });
                await Promise.all(saves);
                await this._editQueue.idle();
            }
            return !this.editErrorCount && !this.editDirtyCount;
        },
        async resolveStagedBatch() {
            if (this.batchEditing || this.editResolutionPending || !this.canEdit) return;
            const queue = this._editQueue;
            const records = [...queue.records.values()].filter(record => record.proposal !== undefined);
            if (!records.length) return;
            this.editResolutionPending = true;
            try {
                const response = await api.get('/api/timesheets/' + this._editSheetId, {ignoreModal: true});
                if (queue !== this._editQueue) return;
                if (!response.data.permissions.can_edit) throw Error('Табель больше недоступен для изменения. Скопируйте несохранённый ввод.');
                const current = records.map(record => {
                    const line = response.data.lines.find(item => Number(item.id) === record.meta.lineId);
                    if (!line) throw Error('Строка удалена. Скопируйте несохранённый ввод.');
                    const entry = (line.entries || []).find(item => item.work_date === record.meta.date);
                    return {entry, value: this.entryDisplay(entry), version: {version: Number(entry && entry.version || 0), id: Number(entry && entry.id || 0)}};
                });
                const alreadySaved = records.every((record, i) => equal(record.proposal, current[i].value));
                const changed = records.some((record, i) => !equal(record.version, current[i].version));
                let choice = true;
                if (!alreadySaved && changed) choice = await this.$bvModal.msgBoxConfirm(
                    'Значения на сервере изменились. Повторно применить весь диапазон с заменой текущих отметок?',
                    {title: 'Диапазон не сохранён', okTitle: 'Применить весь диапазон', cancelTitle: 'Принять серверные значения', noCloseOnBackdrop: true});
                if (choice === null || queue !== this._editQueue) return;
                records.forEach((record, i) => {
                    queue.rebase(record.key, current[i].value, current[i].version, !alreadySaved && choice);
                    if (alreadySaved || !choice) this.acceptTimesheetEdit(record, {...current[i], sheetVersion: response.data.timesheet.version}, true);
                });
                if (!alreadySaved && choice) {
                    this.batchEditing = true;
                    await this.trackEditPromise(queue.saveBatch(records.map(record => record.key)));
                }
                this.scheduleEditRefresh();
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось проверить диапазон. Ввод не записан.');
                this.arrivalCancelledMessage = this.errorMessage;
            } finally { this.batchEditing = false; this.editResolutionPending = false; }
        },
        async resolveEditProblem(key) {
            if (this._editQueue && this._editQueue.records.get(key) && this._editQueue.records.get(key).proposal !== undefined) return this.resolveStagedBatch();
            if (!this._editQueue || this.editResolutionPending) return;
            const queue = this._editQueue;
            const record = queue.records.get(key);
            if (!record || record.inFlight || record.pending) return;
            this.editResolutionPending = true;
            const sheetId = this._editSheetId;
            const proposedValue = record.proposal === undefined ? record.value : record.proposal;
            try {
                const response = await api.get(`/api/timesheets/${sheetId}`, {ignoreModal: true});
                if (queue !== this._editQueue) return;
                const line = response.data.lines.find(item => Number(item.id) === record.meta.lineId);
                if (!line || !response.data.permissions.can_edit) {
                    const discard = await this.$bvModal.msgBoxConfirm(
                        `Строка больше недоступна для изменения. Ваш ввод: ${record.meta.date ? record.value : JSON.stringify(record.value)}. Скопируйте его перед удалением черновика. Удалить несохранённый черновик?`,
                        {title: 'Несохранённый черновик', okTitle: 'Удалить черновик', cancelTitle: 'Оставить', okVariant: 'danger'}
                    );
                    if (discard && queue === this._editQueue) queue.rebase(key, record.baseline, record.version, false);
                    return;
                }
                const entry = record.meta.date && (line.entries || []).find(item => item.work_date === record.meta.date);
                const value = record.meta.date ? this.entryDisplay(entry) : this.detailEditValue(line);
                const version = record.meta.date ? {version: Number(entry && entry.version || 0), id: Number(entry && entry.id || 0)} : Number(line.details_version || 1);
                // An interrupted response may already have committed precisely this value.
                if (equal(value, proposedValue)) {
                    queue.rebase(key, value, version, false);
                    this.acceptTimesheetEdit(record, {value, version, entry, details: line, sheetVersion: response.data.timesheet.version}, true);
                    this.scheduleEditRefresh();
                    return;
                }
                const choice = await this.$bvModal.msgBoxConfirm(
                    `На сервере: ${record.meta.date ? value || 'пусто' : JSON.stringify(value)}. Ваш ввод: ${record.meta.date ? proposedValue || 'пусто' : JSON.stringify(record.value)}.`,
                    {title: 'Значение не сохранено', okTitle: 'Сохранить моё', cancelTitle: 'Принять серверное',
                        okVariant: 'primary', hideHeaderClose: false, noCloseOnBackdrop: true}
                );
                if (choice === null || queue !== this._editQueue) return;
                queue.rebase(key, value, version, choice === true);
                if (choice) await this.trackEditPromise(queue.save(key));
                else this.acceptTimesheetEdit(record, {value, version, entry, details: line, sheetVersion: response.data.timesheet.version}, true);
                this.scheduleEditRefresh();
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось проверить сохранённое значение. Черновик оставлен.');
            } finally { this.editResolutionPending = false; }
        }
    }
};
